import React from 'react';
import {
  Box,
  Typography,
  Button,
  alpha,
} from '@mui/material';
import type { QuickFillItem, QuickFillRowMode } from '../../../shared/types';
import type { QuickFillLoadProgress } from '../lib/quickFillLoadProgress';
import {
  ActionBar,
  type ActionBarProps,
  QuickFillLoadingPlaceholder,
  type QuickFillLoadingPlaceholderProps,
  QuickFillSidePanels,
  type QuickFillSidePanelsProps,
} from './components';
import QuickFillNavigationDock, { type QuickFillNavigationDockProps } from './components/QuickFillNavigationDock';
import FilterableTable from '../../../shared/ui/table/FilterableTable';
import { WorkspaceStatusPill } from '../../../shared/ui/workflow/WorkflowWorkspacePrimitives';
import { borderRadius, layoutGap } from '../../../theme/theme';

const QUICK_FILL_WORKSPACE_TITLE_ID = 'quickfill-workspace-title';
const QUICK_FILL_WORKSPACE_STATUS_ID = 'quickfill-workspace-status';
const screenReaderOnlySx = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  p: 0,
  m: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
  border: 0,
} as const;

export interface QuickFillWorkspaceProps {
  modelStatusBanner: React.ReactNode;
  contextMenu: React.ReactNode;
  useDrawerPanels: boolean;
  isShortViewport: boolean;
  isTableLoading: boolean;
  quickFillData: QuickFillItem[];
  visibleProperties: Array<{ label: string }>;
  rowMode: QuickFillRowMode;
  validationScopeInfo: {
    requestId: string;
    title: string;
    summary: string;
    affectedCount: number;
    affectedItems: string[];
  } | null;
  onDismissValidationScope: () => void;
  showQuickFillApplyProgress: boolean;
  quickFillApplyProgressMessage: string;
  quickFillApplyProgressDetail: string;
  activeLoadProgressText: string;
  isInitializing: boolean;
  isBuilding: boolean;
  buildProgress: string;
  activeLoadProgress: QuickFillLoadProgress | null;
  actionBarRef: React.RefObject<HTMLDivElement | null>;
  tableAreaRef: React.RefObject<HTMLDivElement | null>;
  navigationDockRef: React.RefObject<HTMLDivElement | null>;
  onTableContextMenu: (event: React.MouseEvent) => void;
  actionBar: ActionBarProps;
  table: React.ComponentProps<typeof FilterableTable>;
  shouldRenderTableSurface: boolean;
  loadingPlaceholder: QuickFillLoadingPlaceholderProps;
  navigationDock: QuickFillNavigationDockProps | null;
  sidePanels: QuickFillSidePanelsProps;
}

const QuickFillWorkspace: React.FC<QuickFillWorkspaceProps> = ({
  modelStatusBanner,
  contextMenu,
  useDrawerPanels,
  isShortViewport,
  isTableLoading,
  quickFillData,
  visibleProperties,
  rowMode,
  validationScopeInfo,
  onDismissValidationScope,
  showQuickFillApplyProgress,
  quickFillApplyProgressMessage,
  quickFillApplyProgressDetail,
  activeLoadProgressText,
  isInitializing,
  isBuilding,
  buildProgress,
  activeLoadProgress,
  actionBarRef,
  tableAreaRef,
  navigationDockRef,
  onTableContextMenu,
  actionBar,
  table,
  shouldRenderTableSurface,
  loadingPlaceholder,
  navigationDock,
  sidePanels,
}) => (
  <>
    {modelStatusBanner}
    <Box
      component="section"
      aria-labelledby={QUICK_FILL_WORKSPACE_TITLE_ID}
      aria-describedby={QUICK_FILL_WORKSPACE_STATUS_ID}
      aria-busy={isTableLoading}
      sx={{
        width: '100%',
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflowX: 'hidden',
        overflowY: useDrawerPanels ? 'auto' : 'hidden',
        gap: { xs: 0.4, md: 0.35 },
      }}
    >
      <Typography
        id={QUICK_FILL_WORKSPACE_TITLE_ID}
        component="h2"
        sx={screenReaderOnlySx}
      >
        Quick Fill data workspace
      </Typography>
      <Typography
        id={QUICK_FILL_WORKSPACE_STATUS_ID}
        component="p"
        aria-live="polite"
        sx={screenReaderOnlySx}
      >
        {showQuickFillApplyProgress
          ? `Quick Fill is applying changes to Revit. ${quickFillApplyProgressMessage}. ${quickFillApplyProgressDetail}.`
          : isTableLoading
          ? activeLoadProgressText || 'Quick Fill data is loading.'
          : `Quick Fill has ${quickFillData.length} row${quickFillData.length === 1 ? '' : 's'} and ${visibleProperties.length} visible column${visibleProperties.length === 1 ? '' : 's'}.`}
      </Typography>
      {validationScopeInfo ? (
        <Box
          data-testid="quickfill-validation-scope"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: layoutGap.row,
            px: 0.85,
            py: 0.45,
            borderRadius: `${borderRadius.md}px`,
            border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.05),
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: layoutGap.row, minWidth: 0, flexWrap: 'wrap' }}>
            <WorkspaceStatusPill label="Validation scope" tone="accent" />
            <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.4 }}>
              Current scope is limited to this validation issue.
              {quickFillData.length > 0
                ? ` ${quickFillData.length} ${rowMode === 'instance' ? 'rows' : 'family/type rows'} in view.`
                : ''}
            </Typography>
          </Box>
          <Button
            size="small"
            variant="text"
            onClick={onDismissValidationScope}
            aria-label="Show all Quick Fill components"
            sx={{ minWidth: 0, px: 0 }}
          >
            Show all components
          </Button>
        </Box>
      ) : null}
      <Box ref={actionBarRef} sx={{ flexShrink: 0, minWidth: 0 }}>
        <ActionBar {...actionBar} />
      </Box>

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          overflow: 'hidden',
          minHeight: isShortViewport
            ? { xs: 'min(42vh, 260px)', sm: 'min(46vh, 300px)', md: 0 }
            : { xs: 'min(62vh, 460px)', md: 0 },
          gap: 0,
        }}
      >
        <Box
          ref={tableAreaRef}
          onContextMenu={onTableContextMenu}
          sx={{ flex: 1, minWidth: 0, overflow: 'hidden', minHeight: 0, transition: 'width 0.3s ease-in-out', position: 'relative' }}
        >
          {shouldRenderTableSurface ? (
            <FilterableTable {...table} />
          ) : (
            <QuickFillLoadingPlaceholder {...loadingPlaceholder} />
          )}
          {navigationDock ? (
            <Box
              ref={navigationDockRef}
              sx={{
                position: 'absolute',
                right: 0,
                bottom: 0,
                zIndex: 6,
                pointerEvents: 'auto',
              }}
            >
              <QuickFillNavigationDock {...navigationDock} />
            </Box>
          ) : null}
          {contextMenu}
        </Box>

        <QuickFillSidePanels {...sidePanels} />
      </Box>
    </Box>
  </>
);

export default QuickFillWorkspace;
